import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useAuth } from '@/app/contexts/AuthContext';
import { eventRoutes } from '@/data/routes';
import { useGuildData } from '@/hooks/useGuildData';
import {
  listRecentDiscussionActivity,
  listRecentResultActivity,
  type RecentDiscussionActivity,
  type RecentResultActivity,
} from '@/lib/repositories/activityRepository';
import { supabase } from '@/lib/supabase/client';
import type { GuildActivityItem } from '@/types/domain';
import { getProfileInGameName } from '@/utils/profile';

interface GuildActivityFeedContextValue {
  items: GuildActivityItem[];
  isLoading: boolean;
  error: string | null;
  isCollapsed: boolean;
  dismissItem: (id: string) => void;
  clearVisibleItems: () => void;
  toggleCollapsed: () => void;
}

const GuildActivityFeedContext = createContext<GuildActivityFeedContextValue | null>(null);
const COLLAPSED_KEY = 'woof-woof:activity-feed:collapsed';
const DISMISSED_KEY_PREFIX = 'woof-woof:activity-feed:dismissed:';
const RECENT_ACTIVITY_WINDOW_MS = 1000 * 60 * 60 * 24 * 14;

function getDismissedStorageKey(profileId?: string): string {
  return `${DISMISSED_KEY_PREFIX}${profileId ?? 'anonymous'}`;
}

function trimPreview(value: string, maxLength = 88): string {
  const normalized = value.trim();

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

function isRecent(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() <= RECENT_ACTIVITY_WINDOW_MS;
}

export function GuildActivityFeedProvider({ children }: PropsWithChildren): JSX.Element {
  const { authMode, isApproved, isSignedIn, needsOnboarding, hasAnyRole, profile } = useAuth();
  const { state } = useGuildData();
  const [discussionActivity, setDiscussionActivity] = useState<RecentDiscussionActivity[]>([]);
  const [resultActivity, setResultActivity] = useState<RecentResultActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const isStaff = hasAnyRole(['owner', 'admin', 'officer', 'mass_manager']);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedCollapsed = window.localStorage.getItem(COLLAPSED_KEY);
    const defaultCollapsed = window.matchMedia('(max-width: 1320px)').matches;
    setIsCollapsed(storedCollapsed === null ? defaultCollapsed : storedCollapsed === 'true');
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const raw = window.localStorage.getItem(getDismissedStorageKey(profile?.id));

    if (!raw) {
      setDismissedIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as string[];
      setDismissedIds(Array.isArray(parsed) ? parsed : []);
    } catch {
      setDismissedIds([]);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(getDismissedStorageKey(profile?.id), JSON.stringify(dismissedIds.slice(0, 80)));
  }, [dismissedIds, profile?.id]);

  const loadSupplementalActivity = useCallback(async () => {
    if (authMode !== 'supabase' || !isSignedIn || !isApproved || needsOnboarding) {
      setDiscussionActivity([]);
      setResultActivity([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [nextDiscussion, nextResults] = await Promise.all([listRecentDiscussionActivity(), listRecentResultActivity()]);
      setDiscussionActivity(nextDiscussion);
      setResultActivity(nextResults);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to load the guild activity feed.');
    } finally {
      setIsLoading(false);
    }
  }, [authMode, isApproved, isSignedIn, needsOnboarding]);

  useEffect(() => {
    void loadSupplementalActivity();
  }, [loadSupplementalActivity]);

  useEffect(() => {
    if (authMode !== 'supabase' || !supabase || !isSignedIn || !isApproved || needsOnboarding || !profile) {
      return undefined;
    }

    const client = supabase;
    let refreshTimeout: number | undefined;

    const scheduleRefresh = () => {
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }

      refreshTimeout = window.setTimeout(() => {
        void loadSupplementalActivity();
      }, 500);
    };

    const channel = client
      .channel(`guild-activity-feed-${profile.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_discussion_messages' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_result_assets' }, scheduleRefresh)
      .subscribe();

    return () => {
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }

      void client.removeChannel(channel);
    };
  }, [authMode, isApproved, isSignedIn, loadSupplementalActivity, needsOnboarding, profile]);

  const items = useMemo<GuildActivityItem[]>(() => {
    const profileById = new Map(state.profiles.map((entry) => [entry.id, entry]));
    const eventById = new Map(state.events.map((entry) => [entry.id, entry]));
    const slotById = new Map(state.eventSlots.map((entry) => [entry.id, entry]));
    const movedSignupIds = new Set(
      state.auditLogs
        .filter((entry) => entry.action === 'signup_moved_by_staff' && typeof entry.entityId === 'string')
        .map((entry) => entry.entityId as string),
    );

    const publishedItems: GuildActivityItem[] = state.events
      .filter((event) => event.status !== 'draft' && isRecent(event.createdAt))
      .map((event) => ({
        id: `mass-published:${event.id}`,
        kind: 'mass_published',
        eventId: event.id,
        title: 'New mass published',
        description: `${event.title} is live in the schedule.`,
        createdAt: event.createdAt,
        href: eventRoutes.detail(event.id),
        visibility: 'approved',
      }));

    const signupItems: GuildActivityItem[] = state.signups
      .filter(
        (signup) =>
          (signup.status === 'joined' || signup.status === 'waitlist') &&
          !movedSignupIds.has(signup.id) &&
          isRecent(signup.createdAt),
      )
      .map((signup) => {
        const event = eventById.get(signup.eventId);
        const slot = slotById.get(signup.eventSlotId);
        const member = profileById.get(signup.profileId);
        const memberName = getProfileInGameName(member);
        const partyLabel = slot ? `Party ${slot.partyNumber}` : 'the mass roster';
        const actionLabel = signup.status === 'waitlist' ? 'joined the waitlist for' : 'signed up for';

        return {
          id: `signup:${signup.id}`,
          kind: signup.status === 'waitlist' ? 'mass_waitlist' : 'mass_signup',
          actorProfileId: signup.profileId,
          eventId: signup.eventId,
          title: signup.status === 'waitlist' ? 'Mass waitlist updated' : 'Mass signup',
          description: `${memberName} ${actionLabel} ${slot?.weaponName ?? 'a slot'} in ${partyLabel}${event ? ` for ${event.title}` : ''}.`,
          createdAt: signup.createdAt,
          href: event ? eventRoutes.detailWithContext(event.id, { focus: 'slot', slotId: signup.eventSlotId, signupId: signup.id }) : undefined,
          visibility: 'approved',
        };
      });

    const movedItems: GuildActivityItem[] = state.auditLogs
      .filter((entry) => entry.action === 'signup_moved_by_staff' && isRecent(entry.createdAt))
      .map((entry) => {
        const metadata = entry.metadata ?? {};
        const eventId = typeof metadata.event_id === 'string' ? metadata.event_id : undefined;
        const targetSlotId = typeof metadata.target_event_slot_id === 'string' ? metadata.target_event_slot_id : undefined;
        const movedProfileId = typeof metadata.profile_id === 'string' ? metadata.profile_id : undefined;
        const event = eventId ? eventById.get(eventId) : undefined;
        const targetSlot = targetSlotId ? slotById.get(targetSlotId) : undefined;
        const actor = entry.actorProfileId ? profileById.get(entry.actorProfileId) : undefined;
        const movedMember = movedProfileId ? profileById.get(movedProfileId) : undefined;
        const actorName = actor ? getProfileInGameName(actor) : 'Staff';
        const movedName = movedMember ? getProfileInGameName(movedMember) : 'A member';

        return {
          id: `audit:${entry.id}`,
          kind: 'staff_signup_moved',
          actorProfileId: entry.actorProfileId,
          eventId,
          title: 'Roster updated',
          description: `${actorName} moved ${movedName}${targetSlot ? ` to Party ${targetSlot.partyNumber} ${targetSlot.weaponName}` : ''}${event ? ` for ${event.title}` : ''}.`,
          createdAt: entry.createdAt,
          href: eventId && targetSlotId ? eventRoutes.detailWithContext(eventId, { focus: 'slot', slotId: targetSlotId }) : eventId ? eventRoutes.detail(eventId) : undefined,
          visibility: 'staff',
        };
      });

    const discussionItems: GuildActivityItem[] = discussionActivity
      .filter((entry) => isRecent(entry.createdAt))
      .map((entry) => {
        const event = eventById.get(entry.eventId);
        const actor = profileById.get(entry.profileId);
        const actorName = getProfileInGameName(actor);

        return {
          id: `discussion:${entry.id}`,
          kind: 'discussion_message',
          actorProfileId: entry.profileId,
          eventId: entry.eventId,
          title: 'Finished mass discussion',
          description: `${actorName}: ${trimPreview(entry.content)}${event ? ` (${event.title})` : ''}`,
          createdAt: entry.createdAt,
          href: event ? eventRoutes.detailWithContext(event.id, { focus: 'discussion', messageId: entry.id }) : undefined,
          visibility: 'approved',
        };
      });

    const resultItems: GuildActivityItem[] = resultActivity
      .filter((entry) => isRecent(entry.createdAt))
      .map((entry) => {
        const event = eventById.get(entry.eventId);
        const actor = profileById.get(entry.uploaderProfileId);
        const actorName = getProfileInGameName(actor);

        return {
          id: `result:${entry.id}`,
          kind: 'result_uploaded',
          actorProfileId: entry.uploaderProfileId,
          eventId: entry.eventId,
          title: 'Result image uploaded',
          description: `${actorName} uploaded results${event ? ` for ${event.title}` : ''}${entry.caption ? `: ${trimPreview(entry.caption, 60)}` : '.'}`,
          createdAt: entry.createdAt,
          href: event ? eventRoutes.detailWithContext(event.id, { focus: 'results', resultId: entry.id }) : undefined,
          visibility: 'approved',
        };
      });

    const combined = [...publishedItems, ...signupItems, ...movedItems, ...discussionItems, ...resultItems]
      .filter((item) => (item.visibility === 'staff' ? isStaff : true))
      .filter((item) => !dismissedIds.includes(item.id));

    const deduped = new Map<string, GuildActivityItem>();

    for (const item of combined) {
      if (!deduped.has(item.id)) {
        deduped.set(item.id, item);
      }
    }

    return Array.from(deduped.values())
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .slice(0, 24);
  }, [dismissedIds, discussionActivity, isStaff, resultActivity, state.auditLogs, state.eventSlots, state.events, state.profiles, state.signups]);

  const value = useMemo<GuildActivityFeedContextValue>(
    () => ({
      items,
      isLoading,
      error,
      isCollapsed,
      dismissItem: (id) => {
        setDismissedIds((current) => (current.includes(id) ? current : [id, ...current]));
      },
      clearVisibleItems: () => {
        setDismissedIds((current) => [...new Set([...items.map((item) => item.id), ...current])]);
      },
      toggleCollapsed: () => setIsCollapsed((current) => !current),
    }),
    [error, isCollapsed, isLoading, items],
  );

  return <GuildActivityFeedContext.Provider value={value}>{children}</GuildActivityFeedContext.Provider>;
}

export function useGuildActivityFeed(): GuildActivityFeedContextValue {
  const context = useContext(GuildActivityFeedContext);

  if (!context) {
    throw new Error('useGuildActivityFeed must be used within GuildActivityFeedProvider');
  }

  return context;
}
